import { useState, useRef, useCallback, type KeyboardEvent } from "react";
import { Send, Loader2, Square } from "lucide-react";

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

export default function ChatInput({
  onSend,
  onStop,
  disabled = false,
  loading = false,
  placeholder = "Escribe tu pregunta...",
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    }
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled || loading) return;

    onSend(trimmed);
    setValue("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, disabled, loading, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isDisabled = disabled || loading;
  const canSend = value.trim().length > 0 && !isDisabled;

  return (
    <div className="flex items-end gap-2 p-4 border-t border-line bg-surface">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          adjustHeight();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isDisabled}
        rows={2}
        className="flex-1 resize-none rounded border border-line px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand disabled:bg-surface-100 disabled:cursor-not-allowed placeholder:text-content-muted"
        aria-label="Mensaje para el asistente"
      />
      {loading && onStop ? (
        <button
          onClick={onStop}
          className="flex-shrink-0 w-10 h-10 rounded bg-ink text-white flex items-center justify-center hover:bg-ink/80 transition-colors"
          aria-label="Detener respuesta"
          title="Detener"
        >
          <Square className="w-4 h-4" fill="currentColor" />
        </button>
      ) : (
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="flex-shrink-0 w-10 h-10 rounded bg-brand text-white flex items-center justify-center hover:bg-brand-hover disabled:bg-line-faint disabled:cursor-not-allowed transition-colors"
          aria-label="Enviar mensaje"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
}
